import { useState } from 'react';
import { CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import SubPageHeader from '../SubPageHeader';
import { toast } from 'sonner';
import { api } from '@/api/apiClient';

const HEALTH_CHECKS = [
  { key: 'odoo', label: 'اتصال Odoo' },
  { key: 'auth', label: 'المصادقة' },
  { key: 'models', label: 'صلاحيات النماذج' },
  { key: 'company', label: 'الشركة المحددة' },
  { key: 'currency', label: 'العملة' },
  { key: 'storage', label: 'التخزين المحلي' },
  { key: 'network', label: 'الاتصال بالشبكة' },
];

export default function DiagnosticsSection({ onBack }) {
  const [running, setRunning] = useState(false);
  const [checks, setChecks] = useState([]);
  const [diagResult, setDiagResult] = useState(null);

  const runHealthCheck = async () => {
    setRunning(true);
    setChecks([]);
    setDiagResult(null);

    const results = [];
    const addCheck = (key, label, status, detail) => {
      results.push({ key, label, status, detail });
      setChecks([...results]);
    };

    // Network
    addCheck('network', 'الاتصال بالشبكة', 'pass', navigator.onLine ? 'متصل' : 'غير متصل');
    await new Promise(r => setTimeout(r, 200));

    // Storage
    try {
      localStorage.setItem('_test', '1');
      localStorage.removeItem('_test');
      addCheck('storage', 'التخزين المحلي', 'pass', 'يعمل');
    } catch {
      addCheck('storage', 'التخزين المحلي', 'fail', 'غير متاح');
    }
    await new Promise(r => setTimeout(r, 200));

    // Odoo
    try {
      const res = await api.get('/diagnostics/info');
      if (res?.success) {
        const info = res.data || {};
        addCheck('odoo', 'اتصال Odoo', 'pass', info.odoo_version || 'متصل');
        addCheck('auth', 'المصادقة', 'pass', `UID: ${info.uid}`);
      } else {
        addCheck('odoo', 'اتصال Odoo', 'fail', res?.error);
        addCheck('auth', 'المصادقة', 'fail', 'فشل');
      }
    } catch (e) {
      addCheck('odoo', 'اتصال Odoo', 'fail', e.message);
      addCheck('auth', 'المصادقة', 'fail', 'فشل');
    }
    await new Promise(r => setTimeout(r, 300));

    // Full diagnostics
    try {
      const diagRes = await api.get('/diagnostics/full');
      if (diagRes?.success) {
        const d = diagRes.data || {};
        addCheck('endpoints', 'نقاط النهاية',
          d.endpoints?.length ? 'pass' : 'warn',
          d.endpoints ? `${d.endpoints.length} نقطة` : 'غير متاح');
        addCheck('company', 'المستخدم', d.user ? 'pass' : 'fail', d.user || 'غير محدد');
        addCheck('db', 'قاعدة البيانات', d.db ? 'pass' : 'warn', d.db || 'غير محدد');
        setDiagResult(d);
      } else {
        addCheck('endpoints', 'نقاط النهاية', 'warn', 'تعذر الفحص');
        addCheck('company', 'المستخدم', 'warn', 'تعذر الفحص');
        addCheck('db', 'قاعدة البيانات', 'warn', 'تعذر الفحص');
      }
    } catch (e) {
      addCheck('endpoints', 'نقاط النهاية', 'fail', e.message);
    }

    setRunning(false);
  };

  const copyResults = () => {
    const text = checks.map(c => `${c.status === 'pass' ? '✓' : c.status === 'fail' ? '✗' : '⚠'} ${c.label}: ${c.detail}`).join('\n');
    navigator.clipboard.writeText(text);
    toast.success('تم نسخ النتائج');
  };

  const statusIcon = (status) => {
    if (status === 'pass') return <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />;
    if (status === 'fail') return <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />;
    return <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />;
  };

  const passed = checks.filter(c => c.status === 'pass').length;
  const failed = checks.filter(c => c.status === 'fail').length;

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      <SubPageHeader title="التشخيص والدعم" onBack={onBack} />
      <div className="p-4 space-y-4">

        {/* Health Check */}
        <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
          <p className="font-bold text-sm text-foreground">🩺 فحص شامل للنظام</p>
          <Button onClick={runHealthCheck} disabled={running} className="w-full h-12 rounded-xl gap-2 text-base font-bold">
            {running ? <><Loader2 className="w-4 h-4 animate-spin" />جاري الفحص...</> : '🩺 بدء الفحص الشامل'}
          </Button>

          <AnimatePresence>
            {checks.length > 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
                {/* Summary */}
                {!running && (
                  <div className={`rounded-xl p-3 flex items-center justify-between ${failed > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                    <span className="text-sm font-bold">{failed > 0 ? `${failed} مشاكل` : 'كل شيء يعمل ✓'}</span>
                    <span className="text-xs text-muted-foreground">{passed}/{checks.length} نجح</span>
                  </div>
                )}

                {checks.map((c, i) => (
                  <motion.div key={c.key} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                    className="flex items-center gap-2 py-2 px-3 rounded-xl bg-muted/30">
                    {statusIcon(c.status)}
                    <span className="text-sm font-medium flex-1">{c.label}</span>
                    <span className="text-xs text-muted-foreground">{c.detail}</span>
                  </motion.div>
                ))}

                {!running && (
                  <button onClick={copyResults} className="w-full py-2 text-xs text-primary font-semibold border border-primary/20 rounded-xl hover:bg-primary/5 transition-colors">
                    📋 نسخ النتائج
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Support */}
        <div className="bg-card border border-border rounded-2xl p-4 space-y-1">
          <p className="font-bold text-sm text-foreground mb-3">📞 الدعم الفني</p>
          {[
            ['📧', 'راسل الدعم الفني', () => window.open('mailto:support@company.com')],
            ['📚', 'دليل المستخدم', () => toast.info('قريباً...')],
            ['🐛', 'الإبلاغ عن مشكلة', () => toast.info('قريباً...')],
          ].map(([icon, label, action]) => (
            <button key={label} onClick={action}
              className="w-full flex items-center gap-3 py-3 px-1 border-b border-border last:border-0 hover:bg-muted/30 transition-colors text-right">
              <span className="text-base w-6 text-center">{icon}</span>
              <span className="flex-1 text-sm font-medium text-foreground">{label}</span>
              <span className="text-xs text-muted-foreground">→</span>
            </button>
          ))}
        </div>

      </div>
    </div>
  );
}